import * as XLSX from 'xlsx';
import { Box, Button, Card, Container, MenuItem, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldVehicle, EldVehicleStats } from "src/models";
import useModelData, { deleteById, replaceById } from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";

import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { ColoredIcon } from "src/components/colored-icon";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import { Constants } from "src/constants";
import { formatDecimalTwoAsString } from "src/utils/decimal-two-utils";
import QRCode from 'qrcode';
import { Document, Page as PdfPage, Image, pdf, Text, View } from "@react-pdf/renderer";
import Label from "src/components/label";
import { Violations } from "src/components/violations";
import { useDropzone } from "react-dropzone";
import CustomPopover, { usePopover } from "src/components/custom-popover";
import { EldVehicleModal, formatDtc } from "./_eld_vehicle_modal";


type EldVehiclesProps = {
  eldVehicles?: EldVehicle[],
  eldVehicleStats?: EldVehicleStats[],
  onEldVehicleSelected: (a: EldVehicle) => void
}

const QRCodePdf = (qrCode: string, vin: string) => (
  <Document>
    <PdfPage size={[62, 62]}>
      <View style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Image src={qrCode} style={{ width: "51px", height: "51px" }} />
      </View>
      <Text fixed style={{ fontSize: "4px", width: "100%", textAlign: "center" }}>{vin}</Text>
      <Text fixed style={{ fontSize: "2px", width: "100%", textAlign: "center" }}>Counting EldVehicles</Text>
    </PdfPage>
  </Document>
);

export function printQr(qr: string, vin: string) {
  QRCode.toDataURL(qr, { type: "image/png" }).then((img: any) => {

    pdf(QRCodePdf(img, vin)).toBlob().then(blob => {
      const oHiddFrame = document.createElement("iframe");
      oHiddFrame.onload = function () {
        oHiddFrame?.contentWindow?.focus(); // Required for IE
        oHiddFrame?.contentWindow?.print();
      };
      oHiddFrame.style.position = "fixed";
      oHiddFrame.style.right = "0";
      oHiddFrame.style.bottom = "0";
      oHiddFrame.style.width = "0";
      oHiddFrame.style.height = "0";
      oHiddFrame.style.border = "0";
      oHiddFrame.src = window.URL.createObjectURL(blob);
      document.body.appendChild(oHiddFrame);
    })
  })

}


const EldVehicles = memo(({ eldVehicles, eldVehicleStats, onEldVehicleSelected }: EldVehiclesProps) => {
  const { sortedList, sortData } = useSort(eldVehicles);

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["licensePlate", "licensePlateState", "vehicleMake", "vehicleModel", "eldConnectionInterface", "vehicleUnitNumber", "vin"]);

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Eld Vehicles" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortKey="vehicleUnitNumber" sortData={sortData} >
                Vehicle Number
              </SortableHeader>

              <SortableHeader sortKey="vin" sortData={sortData} >
                Vin
              </SortableHeader>

              <TruxTableCell align="left" />

              <SortableHeader sortKey="mac" sortData={sortData} >
                Mac
              </SortableHeader>

              <SortableHeader sortKey="vehicleMake" sortData={sortData} >
                Make
              </SortableHeader>

              <SortableHeader sortKey="vehicleModel" sortData={sortData} >
                Model
              </SortableHeader>

              <SortableHeader sortKey="licensePlate" sortData={sortData} >
                License Plate
              </SortableHeader>

              <SortableHeader sortKey="licensePlateState" sortData={sortData} >
                License Plate State
              </SortableHeader>

              <SortableHeader sortKey="apiEnabled" align="center" sortData={sortData} >
                API Enabled
              </SortableHeader>

              <SortableHeader sortKey="active" sortData={sortData} >
                Active
              </SortableHeader>
            </TableRow>
          </TableHead>


          <TableBody>
            {filteredList.map((eldVehicle, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onEldVehicleSelected(eldVehicle)}
              sx={{ cursor: "pointer" }}
            >
              <TruxTableCell>
                {eldVehicle.vehicleUnitNumber}
              </TruxTableCell>

              <TruxTableCell>
                {eldVehicle.vin}
              </TruxTableCell>

              <TruxTableCell>
                <RenderDtcs eldVehicle={eldVehicle} eldVehicleStats={eldVehicleStats} />
              </TruxTableCell>

              <TruxTableCell>
                {eldVehicle.mac}
              </TruxTableCell>

              <TruxTableCell>
                {eldVehicle.vehicleMake}
              </TruxTableCell>

              <TruxTableCell>
                {eldVehicle.vehicleModel}
              </TruxTableCell>

              <TruxTableCell>
                {eldVehicle.licensePlate}
              </TruxTableCell>

              <TruxTableCell>
                {eldVehicle.licensePlateState}
              </TruxTableCell>

              <TruxTableCell align="center">
                {eldVehicle.apiEnabled
                  ? <ColoredIcon color="success">
                    <Iconify icon="eva:checkmark-circle-2-fill" width={24} />
                  </ColoredIcon>
                  : <ColoredIcon color="error">
                    <Iconify icon="eva:close-fill" width={24} />
                  </ColoredIcon>}
              </TruxTableCell>

              <TruxTableCell>
                {eldVehicle.active &&
                  <Label variant="filled" color="success" sx={{ cursor: "pointer" }}>Active</Label>}

                {!eldVehicle.active &&
                  <Label variant="filled" color="error" sx={{ cursor: "pointer" }}>Inactive</Label>}
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

function RenderDtcs({eldVehicle, eldVehicleStats}: {eldVehicle: EldVehicle, eldVehicleStats?: EldVehicleStats[]}) {
  const stat = eldVehicleStats?.find(s => s.eldVehicleId === eldVehicle.id);

  if (!stat || !stat.dtcCodes || stat.dtcCodes.length === 0) {
    return undefined;
  }

  return <Violations violations={formatDtc(stat.dtcCodes)} />;
}

type PageModel = {
  eldVehicles?: EldVehicle[],
  eldVehicleStats?: EldVehicleStats[],
}

export default function Page() {
  const settings = useSettingsContext();
  const router = useRouter();
  const dataLoader = useCallback(() => ({
    eldVehicles: axios.get(endpoints.eldVehicles.findAll).then(response => response.data),
    eldVehicleStats: axios.get(endpoints.eldVehicleStats.findAll).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const [isOpen, setIsOpen] = useState(false);

  const selectedEldVehicleModel = useModelSavingData<EldVehicle>(
    endpoints.eldVehicles.update,
    endpoints.eldVehicles.create
  );

  const updateEldVehicle = () => {
    selectedEldVehicleModel.save().then(response => {
      if (response) {
        enqueueSnackbar("Successfully saved the Eld Vehicle");
        const eldVehicle = response.data as EldVehicle;
        updateData("eldVehicles")(replaceById(data!.eldVehicles!, eldVehicle));

        setIsOpen(false);
      }
    })
  }

  const onEldVehicleSelected = useCallback((a: EldVehicle) => {
    selectedEldVehicleModel.set(a);
    setIsOpen(true);
  }, [selectedEldVehicleModel, setIsOpen]);


  const onNewEldVehicleSelected = useCallback(() => {
    selectedEldVehicleModel.set({
      id: 0,
      createdAt: undefined,
      active: true,
      organizationId: 0,
      apiEnabled: true,

      vehicleUnitNumber: "",
      vehicleMake: "",
      vehicleModel: "",
      vehicleYear: "",
      vin: "",
      eldConnectionInterface: "",
      licensePlateState: "",
      licensePlate: "",
      mac: "",
      deviceId: 0,
      engineHours: 0,
	    totalMiles: 0,
      odometerOffset: 0,
      engineHourOffset: 0,
      moving: false,
      movingLastUpdated: undefined,
      simBasedEldType: 0,
    });
    setIsOpen(true);
  }, [selectedEldVehicleModel]);

  const newEldVehicleButton = <Button
    onClick={onNewEldVehicleSelected}
    variant="contained"
    color="success"
    startIcon={<Iconify icon="mingcute:add-line" />}
  >
    New Eld Vehicle
  </Button>;



  const [uploadFileOpen, setUploadFileOpen] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        enqueueSnackbar("The file is in the wrong format. Please reach out to your customer support representative.", { variant: "error" });
      }
      acceptedFiles.forEach(file => {
        // we'll send the file to the backend and receive
        // fuel charge lines back that we can choose to save
        setUploadFileOpen(true);
        setUploadingFile(true);

        axios.postForm(
          endpoints.eldVehicles.uploadApiEnabledFile,
          { file }
        ).then(response => response.data).then((response) => {
          if (data && data.eldVehicles) {
            const updatedVehicles = [...data!.eldVehicles!];
            (response as {id: number, apiEnabled: boolean}[]).forEach(l => {
              const foundVehicle = updatedVehicles.find(v => v.id === l.id);
              if (foundVehicle) {
                foundVehicle.apiEnabled = l.apiEnabled;
              }
            });
            updateData("eldVehicles")(updatedVehicles)

            enqueueSnackbar("Successfully mass updated API Enabled");
          }

          setUploadingFile(false);
        }).catch((e) => {
          setUploadingFile(false);
          console.log(e);
        })
      });
    },
    [setUploadFileOpen, setUploadingFile, updateData, data]
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openDropzoneFileSelect
  } = useDropzone({
    noClick: true, onDrop,
    multiple: false,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    }
  })

  const popover = usePopover();

  const actionButton = <>
    <Button
      onClick={popover.onOpen}
      variant="outlined"
      color="inherit"
      startIcon={<Iconify icon="eva:more-vertical-fill" />}
    >
      Actions
    </Button>

    <CustomPopover
      open={popover.open}
      onClose={popover.onClose}
      arrow="top-left"
    >

      <MenuItem
        onClick={() => {
          if (!data?.eldVehicles) {
            return;
          }
          popover.onClose();

          const workbook = XLSX.utils.book_new();

          const sheet = XLSX.utils.json_to_sheet([
            ["VIN"],
            ...data.eldVehicles.filter(eldVehicle => eldVehicle.apiEnabled).map(eldVehicle => [
              eldVehicle.vin,
            ])
          ], { skipHeader: true, });
          const title = `API Enabled Vehicles`;

          XLSX.utils.book_append_sheet(workbook, sheet, title)

          XLSX.writeFile(workbook, `${title}.xlsx`, { compression: true });
        }}
      >
        <Iconify icon="eva:cloud-download-fill" /> Download API Enable
      </MenuItem>

      <MenuItem
        onClick={() => {
          popover.onClose();
          openDropzoneFileSelect();
        }}
      >
        <Iconify icon="eva:cloud-upload-fill" /> Upload API Enable
      </MenuItem>
    </CustomPopover>
  </>;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.eldVehicles?.length !== 0 &&
          <PageHeader title="Eld Vehicles">
            {actionButton}
            {newEldVehicleButton}
          </PageHeader>
        }

        {data && data?.eldVehicles?.length === 0 &&
          <NoneFound title="No Eld Vehicles Found" message="To add an Eld Vehicle click the 'New Eld Vehicle' button below.">
            {newEldVehicleButton}
          </NoneFound>
        }

        {data && data?.eldVehicles?.length !== 0 && <EldVehicles eldVehicleStats={data.eldVehicleStats} eldVehicles={data.eldVehicles} onEldVehicleSelected={onEldVehicleSelected} />}

        {data && <EldVehicleModal
          selectedEldVehicleModel={selectedEldVehicleModel}
          eldVehicleStats={data.eldVehicleStats || []}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          updateEldVehicle={updateEldVehicle}
        />}
      </DisplayPageData>
    </Container>
  );
}
